import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_MY_ADS } from '../graphql/queries';
import { TOGGLE_AD_STATUS } from '../graphql/mutations';
import Layout from '../components/Layout';
import { PlusCircle, Eye, Pencil, Power } from 'react-bootstrap-icons';
import { Card, Button, Row, Col, Badge } from 'react-bootstrap';

const MyAds: React.FC = () => {
  const [activeTab, setActiveTab] = useState('all');
  const { data, loading, refetch } = useQuery(GET_MY_ADS);
  const [toggleAdStatus] = useMutation(TOGGLE_AD_STATUS);

  const ads = data?.myAds || [];

  const filteredAds = activeTab === 'all'
    ? ads
    : activeTab === 'active'
    ? ads.filter((ad: any) => ad.status === 'active')
    : ads.filter((ad: any) => ad.status === 'disabled');

  const handleToggleStatus = async (adId: string) => {
    try {
      await toggleAdStatus({ variables: { id: adId } });
      refetch();
    } catch (error) {
      console.error('Error toggling ad status:', error);
    }
  };

  const tabs = [
    { id: 'all', name: 'All Ads', count: ads.length },
    { id: 'active', name: 'Active', count: ads.filter((a: any) => a.status === 'active').length },
    { id: 'disabled', name: 'Disabled', count: ads.filter((a: any) => a.status === 'disabled').length },
  ];

  return (
    <Layout showSidebar={false}>
      <div className="container-fluid py-4 pw-page">
        <style>
          {`
            .ad-card {
              transition: transform 0.2s, box-shadow 0.2s;
            }
            .ad-card:hover {
              transform: translateY(-2px);
              box-shadow: 0 4px 12px rgba(0,0,0,0.1);
            }
            .action-btn {
              transition: all 0.2s;
            }
            .action-btn:hover {
              transform: scale(1.1);
            }
            .custom-tab {
              border-bottom: 2px solid transparent;
              cursor: pointer;
            }
            .custom-tab.active {
              border-bottom-color: #0d6efd;
              color: #0d6efd;
            }
          `}
        </style>

        {/* Header */}
        <Row className="mb-4 align-items-center">
          <Col>
            <h1 className="display-6 fw-bold">My Ads</h1>
            <p className="text-muted">Manage your listings</p>
          </Col>
          <Col xs="auto">
            <Link to="/post-ad">
              <Button variant="primary" className="d-flex align-items-center">
                <PlusCircle className="me-2" />
                Post New Ad
              </Button>
            </Link>
          </Col>
        </Row>

        {/* Tabs Card */}
          <Card className="mb-4 pw-card">
          <div className="border-bottom">
            <nav className="d-flex px-3 gap-4">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`custom-tab py-3 px-2 bg-transparent border-0 ${
                    activeTab === tab.id ? 'active' : 'text-muted'
                  }`}
                >
                  {tab.name}
                  <Badge
                    bg={activeTab === tab.id ? 'primary' : 'secondary'}
                    className="ms-2"
                    pill
                  >
                    {tab.count}
                  </Badge>
                </button>
              ))}
            </nav>
          </div>

          <Card.Body className="p-4">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-muted">Loading your ads...</p>
              </div>
            ) : filteredAds.length === 0 ? (
              <div className="text-center py-5">
                <p className="text-muted mb-4">No ads found in this category.</p>
                <Link to="/post-ad">
                  <Button variant="primary" className="d-flex align-items-center mx-auto">
                    <PlusCircle className="me-2" />
                    Post Your First Ad
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="d-flex flex-column gap-3">
                {filteredAds.map((ad: any) => (
                  <Card key={ad.id} className="ad-card bg-light">
                    <Card.Body className="p-3">
                      <Row className="align-items-center g-3">
                        {/* Image */}
                        <Col xs="auto">
                          <div style={{ width: '96px', height: '96px' }}>
                            {ad.imagesList?.[0]?.url ? (
                              <img
                                src={ad.imagesList[0].url}
                                alt={ad.title}
                                className="img-fluid rounded object-fit-cover"
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <div
                                className="bg-secondary rounded d-flex align-items-center justify-content-center"
                                style={{ width: '100%', height: '100%' }}
                              >
                                <span className="text-muted small">No image</span>
                              </div>
                            )}
                          </div>
                        </Col>

                        {/* Info */}
                        <Col className="flex-grow-1">
                          <h5 className="mb-1 fw-semibold">{ad.title}</h5>
                          <p className="text-primary fw-medium mb-2">
                            {ad.price ? `Rs. ${Number(ad.price).toLocaleString()}` : ad.priceType}
                          </p>
                          <div className="d-flex align-items-center gap-3">
                            <Badge
                              bg={ad.status === 'active' ? 'success' : 'secondary'}
                              className="text-uppercase"
                            >
                              {ad.status}
                            </Badge>
                            <span className="text-muted small d-flex align-items-center">
                              <Eye className="me-1" size={16} />
                              {ad.viewCount} views
                            </span>
                            <span className="text-muted small">
                              {new Date(ad.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                        </Col>

                        {/* Actions */}
                        <Col xs="auto">
                          <div className="d-flex gap-2">
                            <Link
                              to={`/ad/${encodeURIComponent(ad.slug)}`}
                              className="btn btn-outline-secondary action-btn"
                              title="View"
                            >
                              <Eye size={18} />
                            </Link>
                            <Link
                              to={`/edit-ad/${ad.id}`}
                              className="btn btn-outline-secondary action-btn"
                              title="Edit"
                            >
                              <Pencil size={18} />
                            </Link>
                            <Button
                              variant={ad.status === 'active' ? 'outline-danger' : 'outline-success'}
                              onClick={() => handleToggleStatus(ad.id)}
                              className="action-btn"
                              title={ad.status === 'active' ? 'Disable' : 'Enable'}
                            >
                              <Power size={18} />
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    </Layout>
  );
};

export default MyAds;
